/**
 * useIdleTimer - Hook pour détecter l'inactivité utilisateur
 *
 * Détecte les événements d'interaction utilisateur (tactile, souris, clavier)
 * et déclenche un callback après un délai d'inactivité configurable.
 *
 * Usage:
 * ```tsx
 * useIdleTimer({
 *   timeoutSeconds: 10,
 *   enabled: true,
 *   onIdle: () => logger.log('User is idle'),
 * });
 * ```
 */
import { useEffect, useRef } from 'react';
import { logger } from '../utils/logger';

interface UseIdleTimerOptions {
  /** Délai d'inactivité en secondes */
  timeoutSeconds: number;
  /** Activer le timer */
  enabled: boolean;
  /** Callback appelé lors du timeout */
  onIdle: () => void;
  /** Événements à surveiller (par défaut: tous les événements UI) */
  events?: string[];
}

const DEFAULT_EVENTS = [
  // Événements tactiles (iPad)
  'touchstart',
  'touchend',
  'touchmove',

  // Événements souris (desktop)
  'mousemove',
  'mousedown',
  'click',

  // Événements clavier
  'keydown',
  'keypress',

  // Événements scroll
  'scroll',
  'wheel',
];

export function useIdleTimer({
  timeoutSeconds,
  enabled,
  onIdle,
  events = DEFAULT_EVENTS,
}: UseIdleTimerOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onIdleRef = useRef(onIdle);

  // Mettre à jour la ref du callback pour éviter les stale closures
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    if (!enabled) {
      // Nettoyer le timer si désactivé
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    const resetTimer = () => {
      // Nettoyer le timer précédent
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Créer un nouveau timer
      timeoutRef.current = setTimeout(() => {
        logger.log(`[useIdleTimer] Timeout atteint (${timeoutSeconds}s) - déclenchement onIdle`);
        onIdleRef.current();
      }, timeoutSeconds * 1000);
    };

    // Logger l'activation
    logger.log(`[useIdleTimer] Activé avec timeout de ${timeoutSeconds}s`);
    logger.log(`[useIdleTimer] Événements surveillés:`, events);

    // Initialiser le timer
    resetTimer();

    // Attacher les event listeners
    events.forEach((event) => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });

      logger.log(`[useIdleTimer] Désactivé`);
    };
  }, [timeoutSeconds, enabled, events]);
}
